import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getProsByService,
  getServices,
  getVirtualCategories,
  categoryNameToSlug,
  ProSummary,
} from "../api";
import ZipPicker, { getStoredZip } from "../components/ZipPicker";

export default function SubcategoryProListPage() {
  const { categorySlug, subcategorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [zip, setZip] = useState<string>(getStoredZip());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inArea, setInArea] = useState<ProSummary[]>([]);
  const [otherInCity, setOtherInCity] = useState<ProSummary[]>([]);
  const [matchType, setMatchType] = useState<"subcategory" | "category" | null>(null);
  const [pageTitle, setPageTitle] = useState("");
  const [parentCategoryName, setParentCategoryName] = useState("");

  // Resolve slugs to actual names, then fetch pros
  useEffect(() => {
    if (!categorySlug || !subcategorySlug) return;

    let cancelled = false;
    setLoading(true);
    setError("");

    (async () => {
      try {
        const [services, virtuals] = await Promise.all([
          getServices(),
          getVirtualCategories(),
        ]);

        // Determine the real category name + the user-clicked subcategory name
        let realCategoryName = "";
        let subcategoryName = "";

        // Virtual category path?
        const virtual = virtuals.find((v) => v.virtualCategoryId === categorySlug);
        if (virtual) {
          const ref = (virtual.subcategoryRefs || []).find(
            (r) => categoryNameToSlug(r.subcategoryName) === subcategorySlug
          );
          if (ref) {
            realCategoryName = ref.categoryName;
            subcategoryName = ref.subcategoryName;
          }
        }

        // Real category path?
        if (!subcategoryName) {
          const real = services.find(
            (s) => categoryNameToSlug(s.name) === categorySlug
          );
          if (real) {
            const sub = (real.subcategories || []).find(
              (s) => categoryNameToSlug(s) === subcategorySlug
            );
            if (sub) {
              realCategoryName = real.name;
              subcategoryName = sub;
            }
          }
        }

        if (!subcategoryName) {
          if (!cancelled) setError("Service not found.");
          return;
        }

        if (cancelled) return;
        setPageTitle(subcategoryName);
        setParentCategoryName(realCategoryName);

        // Fetch pros
        const resp = await getProsByService({
          subcategory: subcategoryName,
          category: realCategoryName,
          zip: zip || undefined,
        });

        if (cancelled) return;
        setInArea(resp.inArea || []);
        setOtherInCity(resp.otherInCity || []);
        setMatchType(resp.matchType);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Couldn't load pros for this service.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [categorySlug, subcategorySlug, zip]);

  function handleProClick(pro: ProSummary) {
    // Phase 2 keeps booking gated — guest is sent to login.
    // Phase 3 will swap this for a guest-friendly booking flow.
    navigate(
      `/login?redirect=${encodeURIComponent(`/explore?proId=${pro.proId}`)}`
    );
  }

  const totalCount = inArea.length + otherInCity.length;

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate(`/browse/${categorySlug}`)}
            className="text-sm text-mouna-blue font-bold whitespace-nowrap"
          >
            ← Back
          </button>
          <div className="flex-1 max-w-xs">
            <ZipPicker zip={zip} onChange={setZip} size="sm" />
          </div>
          <button
            onClick={() => navigate("/login")}
            className="text-sm font-bold text-mouna-blue whitespace-nowrap"
          >
            Sign in
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-navy mb-1">
          {pageTitle || "..."}
        </h1>
        {parentCategoryName && (
          <p className="text-sm text-gray-500 mb-4">in {parentCategoryName}</p>
        )}

        {loading && (
          <div className="text-center text-gray-400 text-sm py-16">
            Finding pros…
          </div>
        )}

        {error && (
          <div className="text-center text-red-600 text-sm py-8">{error}</div>
        )}

        {!loading && !error && (
          <>
            {/* Empty state */}
            {totalCount === 0 && (
              <div className="bg-white rounded-2xl p-8 text-center border border-gray-100">
                <div className="text-3xl mb-2">🤔</div>
                <h3 className="font-display font-bold text-navy mb-2">
                  No pros yet for {pageTitle}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  We're growing our network in your area. Try browsing other
                  services.
                </p>
                <button
                  onClick={() => navigate("/browse")}
                  className="text-sm font-bold text-mouna-blue"
                >
                  ← Browse all services
                </button>
              </div>
            )}

            {/* Match type hint when we fell back to category */}
            {matchType === "category" && totalCount > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-xs text-amber-900">
                No exact matches for {pageTitle}. Showing pros in{" "}
                {parentCategoryName} who can likely help.
              </div>
            )}

            {/* In-area bucket */}
            {inArea.length > 0 && (
              <section className="mb-8">
                <h2 className="font-display text-lg font-bold text-navy mb-3">
                  📍 Pros in your area{zip && ` (${zip})`}
                  <span className="ml-2 text-sm text-gray-500 font-normal">
                    {inArea.length}
                  </span>
                </h2>
                <div className="space-y-3">
                  {inArea.map((p) => (
                    <ProCard key={p.proId} pro={p} onClick={() => handleProClick(p)} />
                  ))}
                </div>
              </section>
            )}

            {/* Other in-city bucket */}
            {otherInCity.length > 0 && (
              <section>
                <h2 className="font-display text-lg font-bold text-navy mb-3">
                  Other Houston pros
                  <span className="ml-2 text-sm text-gray-500 font-normal">
                    {otherInCity.length}
                  </span>
                </h2>
                <p className="text-xs text-gray-500 mb-3">
                  These pros may serve your area on request.
                </p>
                <div className="space-y-3">
                  {otherInCity.map((p) => (
                    <ProCard key={p.proId} pro={p} onClick={() => handleProClick(p)} />
                  ))}
                </div>
              </section>
            )}

            {/* No-ZIP nudge */}
            {!zip && totalCount > 0 && (
              <div className="mt-6 p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-900">
                💡 Add your ZIP code above to see pros local to your area first.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ProCard({ pro, onClick }: { pro: ProSummary; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-white hover:shadow-md transition-all border border-gray-100 text-left"
    >
      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gray-100 flex-shrink-0 overflow-hidden">
        {pro.photoUrl ? (
          <img
            src={pro.photoUrl}
            alt={pro.businessName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xl text-gray-400">
            👤
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-bold text-navy text-sm truncate">
            {pro.businessName}
          </span>
          {pro.verified && (
            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold flex-shrink-0">
              ✓
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-600">
          <span>
            ⭐ {pro.rating.toFixed(1)}
            {pro.reviewCount > 0 && ` (${pro.reviewCount})`}
          </span>
          {pro.hourlyRate > 0 && <span>${pro.hourlyRate}/hr</span>}
        </div>
        {pro.bio && (
          <p className="text-xs text-gray-500 mt-1 line-clamp-1">{pro.bio}</p>
        )}
      </div>
      <span className="text-mouna-blue font-bold text-xs whitespace-nowrap">
        View →
      </span>
    </button>
  );
}
